import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Select_StartPageAction } from "@mirage/analytics/events/types/select_start_page_action";
import { useStacksTabFilterPreference } from "@mirage/mosaics/StartPage/TabbedModule/TabbedPanels/useStacksTabFilterPreference";
import { useSelectedTab } from "@mirage/mosaics/StartPage/TabbedModule/useSelectedTab";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useIsActivityFeedEnabled } from "@mirage/service-feed/hooks/features";
import { StackFilterOptionToPapName } from "@mirage/service-settings/service/types";
import i18n from "@mirage/translations";
import classNames from "classnames";
import * as React from "react";
import { forwardRef, lazy, memo, Suspense, useCallback } from "react";
import styles from "./TabbedModule.module.css";

import type { TabIds } from "@mirage/service-settings/service/types";
import type { Ref } from "react";

const LazyStacksPanel = lazy(() =>
  import("./TabbedPanels/StacksTabPanel").then((module) => ({
    default: module.StacksTabPanel,
  })),
);

const LazyRecentsPanel = lazy(() =>
  import("./TabbedPanels/RecentsTabPanel").then((module) => ({
    default: module.RecentsTabPanel,
  })),
);

const LazyActivityPanel = lazy(() =>
  import("./TabbedPanels/ActivityTabPanel").then((module) => ({
    default: module.ActivityTabPanel,
  })),
);

const SuspenseFallback = () => {
  // Note: we don't show a spiner since panels are using a skeleton and we apply this
  // minHeight to prevent things from shifting around
  return <Box style={{ minHeight: "1000px" }} />;
};

export const TabbedModule = memo(function TabbedModule({
  maxCardsPerRow,
}: {
  maxCardsPerRow: number;
}) {
  const {
    startPageSelectedTab: selectedTab,
    setStartPageSelectedTab: _setSelectedTab,
  } = useSelectedTab();

  // We use a forward ref on the TabHeader so we can save the div that we use to render accessory buttons.
  // We pass that down into each TabPanel which will use react's portal to render any buttons associated with
  //    that panel/module. This keeps the logic cleaner & more contained
  const [headerButtonSlot, setHeaderButtonSlot] = React.useState(null);
  const accessoryButtonRef = React.useRef(null);
  React.useEffect(() => {
    setHeaderButtonSlot(accessoryButtonRef.current);
  }, []);
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { startPageStackFilterPreference } = useStacksTabFilterPreference();
  const isActivityFeedEnabled = useIsActivityFeedEnabled();

  const onTabSelection = useCallback(
    (tabId: TabIds) => {
      if (tabId === "recents") {
        reportPapEvent(
          PAP_Select_StartPageAction({
            startPageAction: "click_recents_tab",
            featureLine: "recents",
            actionSurfaceComponent: "recents",
          }),
        );
      }
      if (tabId === "stacks") {
        reportPapEvent(
          PAP_Select_StartPageAction({
            startPageAction: "click_stacks_tab",
            featureLine: "stacks",
            actionSurfaceComponent: "stacks",
            dashStackFilterName:
              StackFilterOptionToPapName[startPageStackFilterPreference],
          }),
        );
      }
      if (tabId === "activity") {
        reportPapEvent(
          PAP_Select_StartPageAction({
            startPageAction: "click_activity_tab",
            featureLine: "activity_feed",
            actionSurfaceComponent: "activity_feed",
          }),
        );
      }

      _setSelectedTab(tabId);
    },
    [startPageStackFilterPreference, reportPapEvent],
  );

  return (
    <Box>
      <TabHeader
        ref={accessoryButtonRef}
        selectedTab={selectedTab}
        onTabSelection={onTabSelection}
      />
      {isActivityFeedEnabled && selectedTab === "activity" && (
        <Suspense fallback={<SuspenseFallback />}>
          <LazyActivityPanel
            isActive={selectedTab === "activity"}
            headerButtonSlot={
              selectedTab === "activity" ? headerButtonSlot : null
            }
          />
        </Suspense>
      )}

      {selectedTab === "recents" && (
        <Suspense fallback={<SuspenseFallback />}>
          <LazyRecentsPanel
            isActive={selectedTab === "recents"}
            headerButtonSlot={
              selectedTab === "recents" ? headerButtonSlot : null
            }
          />
        </Suspense>
      )}

      {selectedTab === "stacks" && (
        <Suspense fallback={<SuspenseFallback />}>
          <LazyStacksPanel
            isActive={selectedTab === "stacks"}
            maxCardsPerRow={maxCardsPerRow}
            headerButtonSlot={
              selectedTab === "stacks" ? headerButtonSlot : null
            }
          />
        </Suspense>
      )}
    </Box>
  );
});

const TabHeader = forwardRef(
  (
    {
      selectedTab,
      onTabSelection,
    }: {
      selectedTab: TabIds;
      onTabSelection: (tabId: TabIds) => void;
    },
    ref: Ref<HTMLDivElement>,
  ) => {
    const isStacksPageTabFeatureEnabled = useFeatureFlagValueBool(
      "dash_web_2025_03_27_start_page_tabs",
    );
    const tabLabels: Array<{ label: string; tabId: TabIds }> = [
      ...(isStacksPageTabFeatureEnabled
        ? [{ label: i18n.t("activity"), tabId: "activity" as TabIds }]
        : []),
      { label: i18n.t("recents"), tabId: "recents" as TabIds },
      { label: i18n.t("stacks"), tabId: "stacks" as TabIds },
    ];

    return (
      <div className={styles.tabHeaderContainer} role="tablist">
        {tabLabels.map(({ label, tabId }) => (
          <div
            role="tab"
            aria-selected={selectedTab === tabId}
            aria-labelledby={label}
            tabIndex={0}
            className={styles.tabLabelWrapper}
            onClick={() => onTabSelection(tabId)}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                onTabSelection(tabId);
              }
            }}
            key={tabId}
          >
            <div
              className={classNames(styles.tabLabelUnderline, {
                [styles.isSelected]: tabId === selectedTab,
              })}
            >
              <Text isBold color={selectedTab === tabId ? "standard" : "faint"}>
                {label}
              </Text>
            </div>
          </div>
        ))}

        {/* Target div used for portal-ing accessory buttons*/}
        <Box marginLeft="auto" display="flex" alignItems="center" ref={ref} />
      </div>
    );
  },
);
TabHeader.displayName = "TabHeader";
